import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RepeatIcon from "@mui/icons-material/Repeat";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import useAppSelector from "../../hooks/useAppSelector";
import * as api from "../../distflowAPI/plannerApi";
import { EventEditor, TaskEditor } from "./Editors";
import Calendar, { monthRange } from "./Calendar";
import {
  dateKey,
  errorMessage,
  formatDate,
  refreshReminders,
  repeatLabels,
  statusLabels,
} from "./helpers";

const routes = {
  mine: "/my-checklist",
  team: "/team-tasks",
  calendar: "/calendar",
};
function TaskCard({ task, team, busy, onOpen, onComplete, onStatus }) {
  const today = dateKey(new Date());
  const late = task.due_date && task.due_date < today && task.status !== "done";
  const checked = task.checklist.filter((step) => step.done).length;
  return (
    <Paper
      component="article"
      variant="outlined"
      sx={{ p: 2, borderRadius: 2, opacity: task.status === "done" ? 0.7 : 1 }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Checkbox
          checked={task.status === "done"}
          disabled={!task.can_edit || busy}
          onChange={() =>
            task.status === "done" ? onStatus(task, "todo") : onComplete(task)
          }
          inputProps={{
            "aria-label": `${task.status === "done" ? "Reopen" : "Complete"} ${task.title}`,
          }}
          sx={{ p: 0.5, mt: 0.25 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Button
            onClick={() => onOpen(task)}
            sx={{
              p: 0,
              justifyContent: "flex-start",
              textAlign: "left",
              textTransform: "none",
              whiteSpace: "normal",
              color: "text.primary",
              minWidth: 0,
              fontSize: 15,
              fontWeight: 600,
              overflowWrap: "anywhere",
              textDecoration: task.status === "done" ? "line-through" : "none",
            }}
          >
            {task.title}
          </Button>
          {task.created_by !== task.assigned_to && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Assigned by {task.creator_name}
            </Typography>
          )}
          <Stack
            direction="row"
            gap={0.75}
            flexWrap="wrap"
            alignItems="center"
            mt={1}
          >
            {task.visibility === "personal" && (
              <Chip
                icon={<LockOutlinedIcon />}
                label="Personal"
                size="small"
                variant="outlined"
              />
            )}
            {team && <Chip label={task.assigned_name} size="small" />}
            {task.due_date && (
              <Typography
                variant="caption"
                color={late ? "error" : "text.secondary"}
              >
                {late ? "Overdue · " : ""}
                {formatDate(task.due_date)}
              </Typography>
            )}
            {task.recurrence !== "none" && (
              <Chip
                icon={<RepeatIcon />}
                label={repeatLabels[task.recurrence]}
                size="small"
                variant="outlined"
              />
            )}
            {!team && task.status === "progress" && (
              <Chip
                size="small"
                label="In progress"
                color="info"
                variant="outlined"
              />
            )}
          </Stack>
          {task.checklist.length > 0 && (
            <Box mt={1.5}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Checklist
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {checked}/{task.checklist.length}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={(checked / task.checklist.length) * 100}
                sx={{ mt: 0.5, height: 4, borderRadius: 1 }}
              />
            </Box>
          )}
          {team && task.status !== "done" && (
            <TextField
              select
              SelectProps={{ native: true }}
              label="Status"
              size="small"
              fullWidth
              value={task.status}
              onChange={(e) => onStatus(task, e.target.value)}
              disabled={!task.can_edit || busy}
              sx={{ mt: 2 }}
              inputProps={{ "aria-label": `Status for ${task.title}` }}
            >
              <option value="todo">To do</option>
              <option value="progress">In progress</option>
              <option value="done">Done</option>
            </TextField>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

export default function Planner({ view = "mine" }) {
  const user = useAppSelector((state) => state.user.authUser);
  const [tasks, setTasks] = useState([]);
  const [people, setPeople] = useState([]);
  const [entries, setEntries] = useState([]);
  const [month, setMonth] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("open");
  const [personFilter, setPersonFilter] = useState("");
  const [taskEditor, setTaskEditor] = useState(null);
  const [eventEditor, setEventEditor] = useState(null);
  const [busy, setBusy] = useState(null);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const generation = useRef({ value: 0 });
  const range = monthRange(month);
  const load = useCallback(
    async (pageNumber = 1) => {
      const run = ++generation.current.value;
      setLoading(true);
      setError("");
      try {
        if (view === "calendar") {
          const { data } = await api.getCalendar(range.start, range.end);
          if (run === generation.current.value) setEntries(data);
        } else {
          const { data } = await api.listTasks({
            scope: view,
            page: pageNumber,
            page_size: 100,
          });
          if (run === generation.current.value) {
            setTasks((old) =>
              pageNumber === 1 ? data.results : [...old, ...data.results],
            );
            setCount(data.count);
            setPage(pageNumber);
          }
        }
      } catch (e) {
        if (run === generation.current.value) setError(errorMessage(e));
      } finally {
        if (run === generation.current.value) setLoading(false);
      }
    },
    [view, range.start, range.end],
  );
  useEffect(() => {
    const requests = generation.current;
    load();
    return () => {
      requests.value++;
    };
  }, [load]);
  useEffect(() => {
    let active = true;
    api
      .listPeople()
      .then(({ data }) => {
        if (active) setPeople(data);
      })
      .catch((e) => {
        if (active) setError(errorMessage(e));
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    setSearch("");
    setFilter("open");
    setPersonFilter("");
  }, [view]);
  async function complete(task) {
    setBusy(task.id);
    setError("");
    try {
      const { data } = await api.completeTask(task.id, task.due_date);
      setTasks((old) => old.map((t) => (t.id === data.id ? data : t)));
      setNotice(
        data.recurrence !== "none"
          ? `Completed. Next due ${formatDate(data.due_date)}.`
          : "Task completed.",
      );
      refreshReminders();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }
  async function changeStatus(task, status) {
    if (status === "done") return complete(task);
    setBusy(task.id);
    setError("");
    try {
      const { data } = await api.updateTask(task.id, {
        status,
        ...(task.updated_at ? { version: task.updated_at } : {}),
      });
      setTasks((old) => old.map((t) => (t.id === data.id ? data : t)));
      refreshReminders();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }
  async function openEntry(entry) {
    setError("");
    try {
      if (entry.type === "task") {
        const { data } = await api.getTask(entry.id);
        setTaskEditor({ ...data, projected: entry.projected });
      } else {
        const { data } = await api.getEvent(entry.id);
        setEventEditor(data);
      }
    } catch (e) {
      setError(errorMessage(e));
    }
  }
  function saved(kind) {
    setTaskEditor(null);
    setEventEditor(null);
    setNotice(`${kind} saved.`);
    refreshReminders();
    load();
  }
  const today = dateKey(new Date());
  const shown = tasks.filter((task) => {
    if (
      search &&
      !`${task.title} ${task.assigned_name} ${task.description}`
        .toLowerCase()
        .includes(search.toLowerCase())
    )
      return false;
    if (personFilter && task.assigned_to !== Number(personFilter)) return false;
    if (view === "team") return true;
    if (filter === "done") return task.status === "done";
    if (task.status === "done") return false;
    if (filter === "today") return task.due_date === today;
    if (filter === "overdue") return task.due_date && task.due_date < today;
    if (filter === "upcoming") return task.due_date && task.due_date > today;
    return true;
  });
  const title = {
    mine: "My Checklist",
    team: "Team Tasks",
    calendar: "Calendar",
  }[view];
  const subtitle = {
    mine: "Your reminders, routines, and work assigned to you.",
    team: "Shared work, clear ownership, and progress at a glance.",
    calendar: "Seasonal dates, weekend coverage, and task deadlines.",
  }[view];
  const card = (task) => (
    <TaskCard
      key={task.id}
      task={task}
      team={view === "team"}
      busy={busy === task.id}
      onOpen={setTaskEditor}
      onComplete={complete}
      onStatus={changeStatus}
    />
  );
  return (
    <Box sx={{ maxWidth: 1450, mx: "auto" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={650}>
            {title}
          </Typography>
          <Typography color="text.secondary" mt={0.75}>
            {subtitle}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {view === "calendar" && (
            <Button variant="outlined" onClick={() => setTaskEditor({})}>
              Add task
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() =>
              view === "calendar"
                ? setEventEditor({})
                : setTaskEditor({
                    visibility: view === "team" ? "team" : "personal",
                  })
            }
          >
            {view === "calendar"
              ? "Add event"
              : view === "team"
                ? "Add task"
                : "Add item"}
          </Button>
        </Stack>
      </Stack>
      <Tabs
        value={view}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: "1px solid", borderColor: "divider" }}
      >
        {[
          ["mine", "My Checklist"],
          ["team", "Team Tasks"],
          ["calendar", "Calendar"],
        ].map(([id, label]) => (
          <Tab
            key={id}
            value={id}
            label={label}
            component={Link}
            to={routes[id]}
          />
        ))}
      </Tabs>
      <Stack spacing={2}>
        {error && (
          <Alert
            severity="error"
            action={
              <Button onClick={() => load()} color="inherit">
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" onClose={() => setNotice("")}>
            {notice}
          </Alert>
        )}
        {view === "calendar" ? (
          <>
            {loading && <LinearProgress />}
            <Calendar
              month={month}
              onMonth={setMonth}
              entries={entries}
              onEntry={openEntry}
              onAddDate={(day) =>
                setEventEditor({ start_date: day, end_date: day })
              }
              loading={loading}
            />
          </>
        ) : (
          <>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={2}
              justifyContent="space-between"
            >
              {view === "mine" ? (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {[
                    ["open", "All open"],
                    ["today", "Today"],
                    ["overdue", "Overdue"],
                    ["upcoming", "Upcoming"],
                    ["done", "Completed"],
                  ].map(([id, label]) => (
                    <Chip
                      key={id}
                      label={label}
                      onClick={() => setFilter(id)}
                      color={filter === id ? "primary" : "default"}
                      variant={filter === id ? "filled" : "outlined"}
                      sx={{
                        bgcolor: filter === id ? "primary.main" : "transparent",
                        color: filter === id ? "#fff" : "text.primary",
                        "&:hover": {
                          bgcolor:
                            filter === id ? "primary.dark" : "action.hover",
                        },
                      }}
                    />
                  ))}
                </Stack>
              ) : (
                <TextField
                  select
                  SelectProps={{ native: true }}
                  size="small"
                  label="Assignee"
                InputLabelProps={{ shrink: true }}
                  value={personFilter}
                  onChange={(e) => setPersonFilter(e.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  <option value="">Everyone</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </TextField>
              )}
              <TextField
                size="small"
                label="Search tasks"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Stack>
            {loading && <LinearProgress />}
            {view === "team" ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                  alignItems: "start",
                }}
              >
                {Object.entries(statusLabels).map(([status, label]) => (
                  <Box
                    key={status}
                    sx={{
                      bgcolor: "#f5f7fa",
                      borderRadius: 2,
                      p: 1.5,
                      minHeight: 210,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      mb={2}
                      px={0.5}
                    >
                      <Typography fontWeight={650}>{label}</Typography>
                      <Chip
                        size="small"
                        label={shown.filter((t) => t.status === status).length}
                      />
                    </Stack>
                    <Stack spacing={1.5}>
                      {shown.filter((t) => t.status === status).map(card)}
                      {!shown.some((t) => t.status === status) && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          textAlign="center"
                          py={4}
                        >
                          No tasks here
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {shown.map(card)}
                {!loading && !shown.length && (
                  <Paper variant="outlined" sx={{ p: 5, textAlign: "center" }}>
                    <Typography variant="h6">
                      {filter === "done"
                        ? "No completed items yet"
                        : "Nothing on this list"}
                    </Typography>
                    <Typography color="text.secondary" mt={1}>
                      {search
                        ? "Try another search."
                        : "Add an item or choose another filter."}
                    </Typography>
                  </Paper>
                )}
              </Stack>
            )}
            <Divider />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" color="text.secondary">
                {tasks.length} of {count} items loaded
                {tasks.length < count
                  ? " · Load more to include older items in filters."
                  : ""}
              </Typography>
              {tasks.length < count && (
                <Button disabled={loading} onClick={() => load(page + 1)}>
                  Load more
                </Button>
              )}
            </Stack>
          </>
        )}
      </Stack>
      {busy && (
        <Box
          role="status"
          aria-label="Saving task"
          sx={{ position: "fixed", bottom: 20, right: 20 }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      {taskEditor && (
        <TaskEditor
          item={taskEditor}
          people={people}
          user={user}
          onClose={() => setTaskEditor(null)}
          onSaved={() => saved("Task")}
          onDeleted={() => saved("Task deletion")}
        />
      )}
      {eventEditor && (
        <EventEditor
          item={eventEditor}
          people={people}
          onClose={() => setEventEditor(null)}
          onSaved={() => saved("Event")}
          onDeleted={() => saved("Event deletion")}
        />
      )}
    </Box>
  );
}
